import { useCallback, useState, useEffect } from 'react'
import Button from 'components/Button'
import Spinner from 'components/Spinner'
import { useEmailPreview } from './use-email-preview'
import { CommsDataTypes } from 'types/comms'
import { resendEmails, getEmrActionAttributeListAPI, sendCommsEmailWithRequestBody } from 'api/comms'
import { toast } from 'react-toastify'
import { useQueryClient } from 'react-query'

import useMemberStore from 'store/member'

export const EmailPreview = ({
  commsData,
  onCloseModal,
}: {
  commsData: CommsDataTypes
  onCloseModal: () => void
}): JSX.Element => {
  const [isHtmlView, isHtmlViewSet] = useState(true)
  const [isSending, setIsSending] = useState(false)
  const [isEditing, setIsEditing] = useState(false)
  const [editedAttributes, setEditedAttributes] = useState<Record<string, string>>({})

  const member = useMemberStore((state) => state.member)
  const [toEmail, setToEmail] = useState(member?.email ?? '');

  const { data: dataEmailPreview, isLoading: isLoadingEmailPreview } =
    useEmailPreview({ actionId: commsData.id })

  const [emrAttributes, setEmrAttributes] = useState<{ name: string; label: string | null; value: string | null }[]>([])

  const queryClient = useQueryClient()

  useEffect(() => {
    async function fetchAttributes() {
      try {
        const response = await getEmrActionAttributeListAPI({ actionId: commsData.id })
        if (response.data) {
          setEmrAttributes(
            response.data.map((item) => ({
              name: item.attributeKey || '',
              label: item.attributeValue || '',
              value: item.emrValue,
            }))
          )
          const initialEdited: Record<string, string> = {}
          response.data.forEach((item) => {
            initialEdited[item.attributeKey || ''] = item.emrValue || ''
          })
          setEditedAttributes(initialEdited)
        }
      } catch (error) {
        toast.error('Failed to load template variables')
      }
    }
    fetchAttributes()
  }, [commsData.id])

  const handleViewToggle = useCallback(() => {
    isHtmlViewSet(!isHtmlView)
  }, [isHtmlView, isHtmlViewSet])

  const handleEditToggle = () => {
    setToEmail(member?.email || commsData.email || '')
    setIsEditing(!isEditing)
  }

  const handleAttributeChange = (key: string, value: string) => {
    setEditedAttributes((prev) => ({ ...prev, [key]: value }))
  }

  // Resend without edits
  const handleResend = useCallback(async () => {
    setIsSending(true)
    try {
      await resendEmails(commsData.id)
      queryClient.invalidateQueries(['useComms'])
      toast.success('Email sent successfully')
      onCloseModal()
    } catch (error) {
      toast.error('Failed to resend email')
    }
    setIsSending(false)
  }, [commsData.id, onCloseModal, queryClient])

  // Resend with edited variables
  const handleResendEdited = useCallback(async () => {
    setIsSending(true)
    try {
      const requestBody: Record<string, string> = {}
      Object.entries(editedAttributes).forEach(([key, value]) => {
        if (key) requestBody[key.toLowerCase()] = value
      })

      const broadcastTime = new Date().toLocaleString('sv-SE', {
        timeZone: 'Pacific/Auckland'
      }).replace(' ', 'T') + 'Z'
      await sendCommsEmailWithRequestBody({
        traderId: member?.traderId || 0,
        actionId: commsData.actionId,
        email: toEmail,
        broadcastTime,
        variables: requestBody,
      })

      queryClient.invalidateQueries(['useComms'])
      toast.success('Email sent successfully with edited variables')
      onCloseModal()
    } catch (error) {
      setToEmail(member?.email || commsData.email || '')
      const resetEdited: Record<string, string> = {}
      emrAttributes.forEach((item) => {
        resetEdited[item.name] = item.value || ''
      })
      setEditedAttributes(resetEdited)
      toast.error('Failed to resend email with edits')
    }
    setIsSending(false)
    setIsEditing(false)
  }, [
    editedAttributes,
    commsData.actionId,
    emrAttributes,
    onCloseModal,
    queryClient,
    toEmail,
    member?.traderId,
    member?.email,
    commsData.email,
  ])

  const htmlTemplate = dataEmailPreview ? dataEmailPreview.htmlPreviewUrl : ''
  const textTemplate = dataEmailPreview ? dataEmailPreview.textPreviewUrl : ''
  const attrTemplates = emrAttributes.map(({ name, label, value }) => ({
    name,
    label,
    value,
  }))

  return (
    <>
      {isLoadingEmailPreview ? (
        <div className="flex justify-center items-center h-52">
          <Spinner />
        </div>
      ) : (
        <div>
          <div className="mb-8 flex gap-2">
            {htmlTemplate && (
              <Button
                onClick={handleViewToggle}
                iconRight={false}
                type="button"
                className="rounded bg-primary tracking-wider font-bold"
              >
                To {isHtmlView ? 'Text View' : 'HTML View'}
              </Button>
            )}
            {!isEditing ? (
              <>
                <Button
                  onClick={handleResend}
                  iconRight={false}
                  type="button"
                  disabled={isSending}
                  className="rounded bg-primary tracking-wider font-bold"
                >
                  {isSending ? 'Sending...' : 'Resend'}
                </Button>
                {attrTemplates.length > 0 && (
                  <Button
                    onClick={handleEditToggle}
                    iconRight={false}
                    type="button"
                    disabled={isSending}
                    className="rounded bg-secondary tracking-wider font-bold"
                  >
                    Edit
                  </Button>
                )}
              </>
            ) : (
              <>
                <Button
                  onClick={handleResendEdited}
                  iconRight={false}
                  type="button"
                  disabled={isSending}
                  className="rounded bg-primary tracking-wider font-bold"
                >
                  {isSending ? 'Sending...' : 'Send Edited'}
                </Button>
                <Button
                  onClick={handleEditToggle}
                  iconRight={false}
                  type="button"
                  disabled={isSending}
                  className="rounded bg-gray-400 tracking-wider font-bold"
                >
                  Cancel
                </Button>
              </>
            )}
          </div>

          <li className="hover:bg-white py-2 px-2 cursor-pointer rounded-xl overflow-hidden list-none text-center">
            {!isEditing ? (
              <>
                <p className="font-bold tracking-wide hidden">{toEmail || 'None'}</p>
              </>
            ) : (
              <>
                <p className="text-lg tracking-wide font-bold">To Email</p>
                <input
                  type="email"
                  value={toEmail || ''}
                  onChange={(e) => setToEmail(e.target.value)}
                  className="w-full rounded px-2 py-1 border border-gray-300 font-light mb-4"
                />
              </>
            )}
          </li>

          <div className="flex gap-8">
            <div className="w-full flex justify-center overflow-y-auto">
              {htmlTemplate && textTemplate ? (
                <div className="w-96 bg-gray-100 bg-opacity-40 rounded-2xl p-4 mb-10 min-h-96">
                  <div>
                    <p className="text-lg tracking-wide font-bold">
                      Template Variables
                    </p>
                    <ul className="flex flex-col text-left">
                      {attrTemplates.map(({ name, label }) => (
                        <li key={name} className="hover:bg-white py-2 px-2 cursor-pointer rounded-xl overflow-hidden">
                          <p className="font-bold text-black text-opacity-40 tracking-wide">[{label}]</p>
                          {!isEditing ? (
                            <p className="font-bold tracking-wide">{editedAttributes[name] || 'None'}</p>
                          ) : (
                            <input
                              type="text"
                              value={editedAttributes[name] || ''}
                              onChange={(e) => handleAttributeChange(name, e.target.value)}
                              className="w-full rounded px-2 py-1 border border-gray-300"
                            />
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              ) : htmlTemplate ? (
                <div className="flex gap-8">
                  <div className="w-full flex justify-center overflow-y-auto">
                    {isHtmlView ? (
                      <div
                        className="bg-white rounded-xl overflow-hidden w-full"
                        style={{ height: '700px' }}
                        dangerouslySetInnerHTML={{
                          __html: `<iframe src='${htmlTemplate}' style="top: 0; left: 0; width: 100%; height: 100%;" />`,
                        }}
                      />
                    ) : (
                      <div
                        className="bg-white rounded-xl overflow-hidden w-full"
                        style={{ height: '700px' }}
                        dangerouslySetInnerHTML={{
                          __html: `<iframe src='${textTemplate}' style="top: 0; left: 0; width: 100%; height: 100%;" />`,
                        }}
                      />
                    )}
                  </div>
                  <div className="w-96 bg-gray-100 bg-opacity-40 rounded-2xl p-4 mb-10 min-h-96">
                    <div>
                      <p className="text-lg tracking-wide font-bold">
                        Template Variables
                      </p>
                      <ul className="flex flex-col">
                        {attrTemplates.map((item) => (
                          <li
                            key={item.name}
                            className="hover:bg-white py-2 px-2 cursor-pointer rounded-xl overflow-hidden"
                          >
                            <p className="font-bold text-black text-opacity-40 tracking-wide">
                              [{item.label}]
                            </p>
                            {isEditing ? (
                              <input
                                type="text"
                                className="w-full border rounded px-2 py-1"
                                value={editedAttributes[item.name] || ''}
                                onChange={(e) =>
                                  handleAttributeChange(item.name, e.target.value)
                                }
                              />
                            ) : (
                              <p className="font-bold tracking-wide">
                                {!item.value || item.value === '' ? 'None' : item.value}
                              </p>
                            )}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              ) : attrTemplates.length > 0 ? (
                // No htmlTemplate, no textTemplate, but variables available
                <div className="text-gray-500 text-lg">
                  <p className="text-center text-black text-lg tracking-wide font-bold">
                    Template Variables
                  </p>
                  <ul className="flex flex-col">
                    {attrTemplates.map((item) => (
                      <li
                        key={item.name}
                        className="hover:bg-white py-2 px-2 cursor-pointer rounded-xl overflow-hidden"
                      >
                        <p className="font-bold text-black text-opacity-40 tracking-wide">
                          [{item.label}]
                        </p>
                        {isEditing ? (
                          <input
                            type="text"
                            className="w-full border rounded px-2 py-1"
                            value={editedAttributes[item.name] || ''}
                            onChange={(e) =>
                              handleAttributeChange(item.name, e.target.value)
                            }
                          />
                        ) : (
                          <p className="font-bold text-black tracking-wide">
                            {!item.value || item.value === '' ? 'None' : item.value}
                          </p>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              ) : (
                <p className="text-center text-gray-500 text-lg">
                  Email Preview is unavailable
                </p>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  )
}